/**
 * Conversion valeurs proposal → px → rem responsive.
 *
 * L'éditeur proposal mélange deux stockages :
 * - unités grille (× gridUnit, ex. marges page 0–16) ;
 * - px directs (lineSpacing, hauteurs espace, padding texte, clientHeight DOM).
 *
 * Toujours passer `field` quand connu ; sinon heuristique alignée sur l'éditeur legacy.
 */

import { gridUnit, lineSpacing as defaultLineSpacing } from '../../constants/gridConfig';
import { formatFontSizeRem } from './proposalRichTextFonts';

export const PROPOSAL_GRID_UNIT = gridUnit;

/** @type {Record<string, 'px' | 'units'>} */
export const PROPOSAL_VALUE_STORAGE_BY_FIELD = {
  lineSpacing: 'px',
  sizeSpaceContainer: 'px',
  paddingTopBottom: 'px',
  paddinngLeftRight: 'px',
  paddingLeftRight: 'px',
  paddingTitle: 'px',
  clientHeight: 'px',
  clientWidth: 'px',
  'margins.left': 'units',
  'margins.right': 'units',
  'margins.top': 'units',
  'margins.bottom': 'units',
};

/**
 * Détecte stockage px vs unités grille.
 * @param {number} value
 * @param {string} [field]
 * @returns {'px' | 'units'}
 */
export function detectProposalValueStorage(value, field) {
  if (field && PROPOSAL_VALUE_STORAGE_BY_FIELD[field]) {
    return PROPOSAL_VALUE_STORAGE_BY_FIELD[field];
  }

  const n = Number(value);
  if (!Number.isFinite(n)) return 'px';

  // Marges page : 0–16 unités grille ; lineSpacing (min UI = 5 px) est toujours
  // passé avec son `field` explicite, donc jamais deviné ici.
  // Zone ambiguë 10–16 : défaut px (espacements) sauf field explicite.
  if (Number.isInteger(n) && n < 10) return 'units';

  return 'px';
}

/**
 * @param {number|string|null|undefined} value
 * @param {string} [field]
 * @param {'px' | 'units' | 'auto'} [storage]
 */
export function proposalValueToPx(value, field, storage = 'auto') {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;

  const resolved = storage === 'auto'
    ? detectProposalValueStorage(n, field)
    : storage;

  return resolved === 'units' ? n * PROPOSAL_GRID_UNIT : n;
}

/** px proposal → rem responsive (comme les tailles de police). */
export function proposalPxToSpacingCss(px) {
  const n = Number(px);
  if (!Number.isFinite(n) || n <= 0) return '';
  return formatFontSizeRem(n / 16);
}

/** Valeur proposal (px ou unités selon champ) → rem responsive. */
export function proposalValueToSpacingCss(value, field, storage = 'auto') {
  return proposalPxToSpacingCss(proposalValueToPx(value, field, storage));
}

/** Marge basse section : lineSpacing (px) + espacements space/line_break (px). */
export function computeSectionMarginBottomCss(
  configuration = {},
  { pendingSpacingPx = 0, trailingSpacingPx = 0 } = {},
) {
  const lineSpacingPx = proposalValueToPx(
    configuration.lineSpacing ?? defaultLineSpacing,
    'lineSpacing',
  );
  return proposalPxToSpacingCss(lineSpacingPx + pendingSpacingPx + trailingSpacingPx);
}

/** Padding bloc texte : top/bottom et left/right séparés, en rem. Rien si 0. */
export function resolveTextPaddingCss(textStyle) {
  if (!textStyle) return {};

  const out = {};
  const toCss = (value, field) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '';
    return proposalValueToSpacingCss(n, field);
  };

  const top = textStyle.paddingTop
    || toCss(textStyle.paddingTopBottom, 'paddingTopBottom');
  const bottom = textStyle.paddingBottom
    || toCss(textStyle.paddingTopBottom, 'paddingTopBottom');
  const left = textStyle.paddingLeft
    || toCss(textStyle.paddingLeftRight, 'paddinngLeftRight');
  const right = textStyle.paddingRight
    || toCss(textStyle.paddingLeftRight, 'paddinngLeftRight');

  if (top) out.paddingTop = top;
  if (bottom) out.paddingBottom = bottom;
  if (left) out.paddingLeft = left;
  if (right) out.paddingRight = right;

  return out;
}

export const DEFAULT_SECTION_MARGIN_BOTTOM_CSS = computeSectionMarginBottomCss({});

/**
 * Longueur CSS ↔ px : les utilisateurs pensent en px, le stockage reste en rem
 * (responsive). Le couple vit dans `shared/boxes/cssLength` — les box
 * partagées avec le document en ont besoin hors de `webviewer/`.
 */
export { cssLengthToPx, pxToRemCss } from '../../shared/boxes/cssLength';
